/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { ExchangeRate } from './ExchangeRate';
import {
    ExchangeRateFromJSON,
    ExchangeRateFromJSONTyped,
    ExchangeRateToJSON,
} from './ExchangeRate';
import type { CountryEnum } from './CountryEnum';
import {
    CountryEnumFromJSON,
    CountryEnumFromJSONTyped,
    CountryEnumToJSON,
} from './CountryEnum';
import type { CustomCheckbox } from './CustomCheckbox';
import {
    CustomCheckboxFromJSON,
    CustomCheckboxFromJSONTyped,
    CustomCheckboxToJSON,
} from './CustomCheckbox';
import type { Product } from './Product';
import {
    ProductFromJSON,
    ProductFromJSONTyped,
    ProductToJSON,
} from './Product';
import type { RuleType } from './RuleType';
import {
    RuleTypeFromJSON,
    RuleTypeFromJSONTyped,
    RuleTypeToJSON,
} from './RuleType';
import type { AdminDistributorProductDistributionInformation } from './AdminDistributorProductDistributionInformation';
import {
    AdminDistributorProductDistributionInformationFromJSON,
    AdminDistributorProductDistributionInformationFromJSONTyped,
    AdminDistributorProductDistributionInformationToJSON,
} from './AdminDistributorProductDistributionInformation';
import type { DistributionPlatformWithPlatformType } from './DistributionPlatformWithPlatformType';
import {
    DistributionPlatformWithPlatformTypeFromJSON,
    DistributionPlatformWithPlatformTypeFromJSONTyped,
    DistributionPlatformWithPlatformTypeToJSON,
} from './DistributionPlatformWithPlatformType';

/**
 * Extends the CampaignSerializer to include distribution information
 * for admin users with appropriate permissions.
 * @export
 * @interface AdminCampaign
 */
export interface AdminCampaign {
    /**
     * 
     * @type {string}
     * @memberof AdminCampaign
     */
    readonly id: string;
    /**
     * 
     * @type {string}
     * @memberof AdminCampaign
     */
    distributionPlatform: string;
    /**
     * 
     * @type {DistributionPlatformWithPlatformType}
     * @memberof AdminCampaign
     */
    readonly distributionPlatformDetails: DistributionPlatformWithPlatformType;
    /**
     * 
     * @type {Product}
     * @memberof AdminCampaign
     */
    readonly token: Product;
    /**
     * 
     * @type {number}
     * @memberof AdminCampaign
     */
    allocation: number;
    /**
     * 
     * @type {number}
     * @memberof AdminCampaign
     */
    readonly allocationAvailable: number;
    /**
     * 
     * @type {Money}
     * @memberof AdminCampaign
     */
    pricePerToken: Money;
    /**
     * 
     * @type {number}
     * @memberof AdminCampaign
     */
    minNumberOfTokens: number;
    /**
     * 
     * @type {number}
     * @memberof AdminCampaign
     */
    maxNumberOfTokens: number;
    /**
     * ISO 3166-1 alpha-3 format
     * @type {CountryEnum}
     * @memberof AdminCampaign
     */
    country: CountryEnum;
    /**
     * 
     * @type {RuleType}
     * @memberof AdminCampaign
     */
    ruleType: RuleType;
    /**
     * 
     * @type {ExchangeRate}
     * @memberof AdminCampaign
     */
    exchangeRate?: ExchangeRate;
    /**
     * 
     * @type {boolean}
     * @memberof AdminCampaign
     */
    active?: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof AdminCampaign
     */
    readonly canActivate: boolean;
    /**
     * Whether legal persons are allowed to invest for this campaign
     * @type {boolean}
     * @memberof AdminCampaign
     */
    legalPersonsAllowed?: boolean;
    /**
     * Whether natural persons are allowed to invest for this campaign
     * @type {boolean}
     * @memberof AdminCampaign
     */
    naturalPersonsAllowed?: boolean;
    /**
     * 
     * @type {string}
     * @memberof AdminCampaign
     */
    name?: string | null;
    /**
     * 
     * @type {number}
     * @memberof AdminCampaign
     */
    readonly signedPercentage: number;
    /**
     * 
     * @type {string}
     * @memberof AdminCampaign
     */
    readonly campaignLink: string;
    /**
     * 
     * @type {Date}
     * @memberof AdminCampaign
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {boolean}
     * @memberof AdminCampaign
     */
    readonly isEcsp: boolean;
    /**
     * 
     * @type {Array<CustomCheckbox>}
     * @memberof AdminCampaign
     */
    readonly customCheckboxes: Array<CustomCheckbox>;
    /**
     * 
     * @type {AdminDistributorProductDistributionInformation}
     * @memberof AdminCampaign
     */
    readonly distributionInformation: AdminDistributorProductDistributionInformation;
}



/**
 * Check if a given object implements the AdminCampaign interface.
 */
export function instanceOfAdminCampaign(value: object): value is AdminCampaign {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('distributionPlatform' in value) || value['distributionPlatform'] === undefined) return false;
    if (!('distributionPlatformDetails' in value) || value['distributionPlatformDetails'] === undefined) return false;
    if (!('token' in value) || value['token'] === undefined) return false;
    if (!('allocation' in value) || value['allocation'] === undefined) return false;
    if (!('allocationAvailable' in value) || value['allocationAvailable'] === undefined) return false;
    if (!('pricePerToken' in value) || value['pricePerToken'] === undefined) return false;
    if (!('minNumberOfTokens' in value) || value['minNumberOfTokens'] === undefined) return false;
    if (!('maxNumberOfTokens' in value) || value['maxNumberOfTokens'] === undefined) return false;
    if (!('country' in value) || value['country'] === undefined) return false;
    if (!('ruleType' in value) || value['ruleType'] === undefined) return false;
    if (!('canActivate' in value) || value['canActivate'] === undefined) return false;
    if (!('signedPercentage' in value) || value['signedPercentage'] === undefined) return false;
    if (!('campaignLink' in value) || value['campaignLink'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('isEcsp' in value) || value['isEcsp'] === undefined) return false;
    if (!('customCheckboxes' in value) || value['customCheckboxes'] === undefined) return false;
    if (!('distributionInformation' in value) || value['distributionInformation'] === undefined) return false;
    return true;
}

export function AdminCampaignFromJSON(json: any): AdminCampaign {
    return AdminCampaignFromJSONTyped(json, false);
}

export function AdminCampaignFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminCampaign {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'distributionPlatform': json['distribution_platform'],
        'distributionPlatformDetails': DistributionPlatformWithPlatformTypeFromJSON(json['distribution_platform_details']),
        'token': ProductFromJSON(json['token']),
        'allocation': json['allocation'],
        'allocationAvailable': json['allocation_available'],
        'pricePerToken': MoneyFromJSON(json['price_per_token']),
        'minNumberOfTokens': json['min_number_of_tokens'],
        'maxNumberOfTokens': json['max_number_of_tokens'],
        'country': CountryEnumFromJSON(json['country']),
        'ruleType': RuleTypeFromJSON(json['rule_type']),
        'exchangeRate': json['exchange_rate'] == null ? undefined : ExchangeRateFromJSON(json['exchange_rate']),
        'active': json['active'] == null ? undefined : json['active'],
        'canActivate': json['can_activate'],
        'legalPersonsAllowed': json['legal_persons_allowed'] == null ? undefined : json['legal_persons_allowed'],
        'naturalPersonsAllowed': json['natural_persons_allowed'] == null ? undefined : json['natural_persons_allowed'],
        'name': json['name'] == null ? undefined : json['name'],
        'signedPercentage': json['signed_percentage'],
        'campaignLink': json['campaign_link'],
        'createdAt': (new Date(json['created_at'])),
        'isEcsp': json['is_ecsp'],
        'customCheckboxes': ((json['custom_checkboxes'] as Array<any>).map(CustomCheckboxFromJSON)),
        'distributionInformation': AdminDistributorProductDistributionInformationFromJSON(json['distribution_information']),
    };
}

export function AdminCampaignToJSON(value?: Omit<AdminCampaign, 'id'|'distribution_platform_details'|'token'|'allocation_available'|'can_activate'|'signed_percentage'|'campaign_link'|'created_at'|'is_ecsp'|'custom_checkboxes'|'distribution_information'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'distribution_platform': value['distributionPlatform'],
        'allocation': value['allocation'],
        'price_per_token': MoneyToJSON(value['pricePerToken']),
        'min_number_of_tokens': value['minNumberOfTokens'],
        'max_number_of_tokens': value['maxNumberOfTokens'],
        'country': CountryEnumToJSON(value['country']),
        'rule_type': RuleTypeToJSON(value['ruleType']),
        'exchange_rate': ExchangeRateToJSON(value['exchangeRate']),
        'active': value['active'],
        'legal_persons_allowed': value['legalPersonsAllowed'],
        'natural_persons_allowed': value['naturalPersonsAllowed'],
        'name': value['name'],
    };
}

